import { useMemo } from "react";
import clsx from "clsx";
import { useStatusSichtbarkeit } from "@/hooks/useStatusSichtbarkeit";
import {
  DOKUMENT_STATUS_LABELS,
  DOKUMENT_TYP_LABELS,
  type DokumentStatus,
  type DokumentTyp,
} from "@/lib/dokumente";

const TYP_OPTIONS: { value: DokumentTyp | "mahnung" | ""; label: string }[] = [
  { value: "", label: "Alle Typen" },
  { value: "angebot", label: "Angebot" },
  { value: "rechnung", label: "Rechnung" },
  { value: "lieferschein", label: "Lieferschein" },
  { value: "auftragsbestaetigung", label: "Auftragsbestätigung" },
  { value: "stundennachweis", label: "Stundennachweis" },
  { value: "stueckliste", label: "Stückliste" },
  { value: "mahnung", label: "Mahnungen" },
];

const ALL_STATUS_OPTIONS: { value: DokumentStatus | ""; label: string }[] = [
  { value: "", label: "Alle Status" },
  { value: "entwurf", label: "Entwurf" },
  { value: "erstellt", label: "Erstellt" },
  { value: "versendet", label: "Versendet" },
  { value: "angenommen", label: "Angenommen" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "abgerechnet", label: "Bezahlt" },
  { value: "geliefert", label: "Geliefert" },
  { value: "storniert", label: "Storniert" },
];

export interface DocumentFilters {
  typ: DokumentTyp | "mahnung" | "";
  status: DokumentStatus | "";
  datumVon: string;
  datumBis: string;
  q: string;
}

export function documentHasActiveFilters(
  filters: DocumentFilters,
  options?: { ignoreTyp?: boolean },
): boolean {
  return Boolean(
    (!options?.ignoreTyp && filters.typ) ||
      filters.status ||
      filters.datumVon ||
      filters.datumBis ||
      filters.q.trim(),
  );
}

export interface DocumentFilterBarProps {
  filters: DocumentFilters;
  onChange: (patch: Partial<DocumentFilters>) => void;
  onReset?: () => void;
  hideTyp?: boolean;
}

export function DocumentFilterBar({ filters, onChange, onReset, hideTyp }: DocumentFilterBarProps) {
  const active = documentHasActiveFilters(filters, { ignoreTyp: hideTyp });
  const { visibleStatuses } = useStatusSichtbarkeit();
  const statusOptions = useMemo(() => {
    const visible = new Set(visibleStatuses("dokumente"));
    return ALL_STATUS_OPTIONS.filter((opt) => opt.value === "" || visible.has(opt.value));
  }, [visibleStatuses]);

  return (
    <section className="rounded-ios-lg bg-card px-4 py-4 shadow-ios-md ring-1 ring-separator/35 dark:ring-separator/50 md:px-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-label">Filter</h3>
        {active && onReset ? (
          <button
            type="button"
            onClick={onReset}
            className="text-xs font-medium text-accent hover:underline"
          >
            Filter zurücksetzen
          </button>
        ) : null}
      </div>

      <div className={clsx("grid gap-3 md:grid-cols-2", hideTyp ? "xl:grid-cols-3" : "xl:grid-cols-4")}>
        {hideTyp ? null : (
          <FilterSelect
            label="Dokumenttyp"
            value={filters.typ}
            onChange={(value) => onChange({ typ: value as DokumentTyp | "mahnung" | "" })}
            options={TYP_OPTIONS}
          />
        )}
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(value) => onChange({ status: value as DokumentStatus | "" })}
          options={statusOptions}
        />
        <label className="block space-y-1 text-sm">
          <span className="text-label-secondary">Belegdatum von</span>
          <input
            type="date"
            value={filters.datumVon}
            onChange={(e) => onChange({ datumVon: e.target.value })}
            className="eh-input w-full"
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-label-secondary">Belegdatum bis</span>
          <input
            type="date"
            value={filters.datumBis}
            onChange={(e) => onChange({ datumBis: e.target.value })}
            className="eh-input w-full"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {filters.typ && !hideTyp ? (
          <FilterChip
            label={filters.typ === "mahnung" ? "Mahnungen" : DOKUMENT_TYP_LABELS[filters.typ]}
            onRemove={() => onChange({ typ: "" })}
          />
        ) : null}
        {filters.status ? (
          <FilterChip label={DOKUMENT_STATUS_LABELS[filters.status]} onRemove={() => onChange({ status: "" })} />
        ) : null}
        {filters.datumVon ? (
          <FilterChip label={`Ab ${filters.datumVon}`} onRemove={() => onChange({ datumVon: "" })} />
        ) : null}
        {filters.datumBis ? (
          <FilterChip label={`Bis ${filters.datumBis}`} onRemove={() => onChange({ datumBis: "" })} />
        ) : null}
      </div>
    </section>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="text-label-secondary">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="eh-input w-full">
        {options.map((opt) => (
          <option key={opt.label} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-fill-tertiary px-2.5 py-1 text-xs text-label-secondary">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className={clsx("rounded-full px-1 text-label-tertiary hover:text-label")}
        aria-label={`${label} entfernen`}
      >
        ×
      </button>
    </span>
  );
}
